import { DGR_DEFAULT_START_HOUR } from './constants';
import { resolveEventOffsetHours } from './country-timezones';

// Returns minutes-since-local-midnight. Falls back to 09:00 when the event
// doesn't have a published start time — every ride day event should still
// land on the timeline even before its individual start time is configured.
export function parseStartTimeToMinutes(startTime: string | null): number {
  if (!startTime) return DGR_DEFAULT_START_HOUR * 60;
  const match = startTime.match(/^(\d{1,2}):(\d{2})/);
  if (!match) return DGR_DEFAULT_START_HOUR * 60;
  const hour = Number(match[1]);
  const minute = Number(match[2]);
  if (!Number.isFinite(hour) || !Number.isFinite(minute)) {
    return DGR_DEFAULT_START_HOUR * 60;
  }
  return hour * 60 + minute;
}

export function computeRideStartUtcMs(
  eventDate: string,
  startTime: string | null,
  offsetHours: number,
): number | null {
  // Trust event_date.slice(0,10) as the local ride day. event_date is stored
  // as UTC midnight of the local day in this dataset, so a direct slice is
  // correct + stable across timezones.
  const datePart = eventDate.slice(0, 10);
  const [y, mo, d] = datePart.split('-').map(Number);
  if (!Number.isFinite(y) || !Number.isFinite(mo) || !Number.isFinite(d)) {
    return null;
  }
  const minutes = parseStartTimeToMinutes(startTime);
  const hour = Math.floor(minutes / 60);
  const minute = minutes % 60;
  const localAsUtcMs = Date.UTC(y, mo - 1, d, hour, minute, 0);
  const offsetMs = offsetHours * 3600 * 1000;
  return localAsUtcMs - offsetMs;
}

export function formatHHMM(ms: number): string {
  const d = new Date(ms);
  return `${String(d.getUTCHours()).padStart(2, '0')}:${String(d.getUTCMinutes()).padStart(2, '0')}`;
}

// Shift a UTC instant into the country's local wall clock, returning HH:MM.
// Purely for display — the underlying timeline still operates in UTC.
export function formatLocalHHMM(utcMs: number, countryCode: string): string {
  const offsetHours = resolveEventOffsetHours(countryCode, utcMs);
  return formatHHMM(utcMs + offsetHours * 3600 * 1000);
}

export function toSlug(value: string) {
  return value
    .toLowerCase()
    .replace(/['']/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}
